import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
 ArrowLeft,
 Search,
 Eye,
 FileText,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { adminVendorDocumentsApi } from '../../services/adminVendorDocumentsService';
import { AdminPageHeader, AdminGuideCallout, AdminEmptyState } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { formatDate } from '../../utils/dateUtils';

const statusChip = (status) => {
 switch (String(status || '').toLowerCase()) {
 case 'paid':
 return 'bg-emerald-50 text-emerald-800 border-emerald-200/80';
 case 'sent':
 case 'pending':
 return 'bg-amber-50 text-amber-800 border-amber-200/80';
 case 'overdue':
 return 'bg-red-50 text-red-800 border-red-200/80';
 default:
 return 'bg-surface-muted text-gray-700 border-gray-200';
 }
};

const STATUS_PILLS = [
 { value: '', label: 'All' },
 { value: 'paid', label: 'Paid' },
 { value: 'sent', label: 'Sent' },
 { value: 'draft', label: 'Draft' },
 { value: 'overdue', label: 'Overdue' },
];

const AdminVendorInvoices = () => {
 const { id } = useParams();
 const [search, setSearch] = useState('');
 const [status, setStatus] = useState('');
 const [page, setPage] = useState(1);

 const { data, isLoading, error } = useQuery({
 queryKey: ['admin-vendor-invoices', id, page, search, status],
 queryFn: () =>
 adminVendorDocumentsApi.getInvoices(id, {
 page,
 per_page: 15,
 search: search || undefined,
 status: status || undefined,
 }),
 enabled: !!id,
 keepPreviousData: true,
 });

 const vendor = data?.vendor;
 const paginator = data?.data;
 const invoices = paginator?.data || [];
 const businessName = vendor?.business_name || vendor?.name || 'Vendor';

 const formatMoney = (amount, currency = 'USD') => {
 const n = Number(amount);
 if (!Number.isFinite(n)) return '—';
 if ((currency || 'USD').toUpperCase() === 'USD') return `$${n.toFixed(2)}`;
 return `${currency} ${n.toFixed(2)}`;
 };

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Vendor invoices"
 subtitle={`${businessName} — invoices and receipts issued to clients.`}
 actions={
 <Link
 to={`/admin/vendors/${id}`}
 className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4 shrink-0" />
 Back to vendor
 </Link>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendorInvoices.title}
 steps={ADMIN_PAGE_GUIDES.vendorInvoices.steps}
 icon={ListChecks}
 />

 <div className="product-panel p-4 sm:p-5 space-y-4">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 >
 {STATUS_PILLS.map((pill) => {
 const active = status === pill.value;
 return (
 <button
 key={pill.value || 'all'}
 type="button"
 onClick={() => {
 setStatus(pill.value);
 setPage(1);
 }}
 className={`px-3 py-1.5 text-sm font-medium !rounded-lg transition-colors ${ active ? 'bg-surface text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900' }`}
 >
 {pill.label}
 </button>
 );
 })}
 </div>

 <div className="relative max-w-sm">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
 <input
 type="text"
 value={search}
 onChange={(e) => {
 setSearch(e.target.value);
 setPage(1);
 }}
 placeholder="Search invoice #, client…"
 className="w-full pl-10 pr-3 py-1.5 text-xs rounded-md border border-border bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-ink"
 />
 </div>
 </div>

 {isLoading ? (
 <div className="product-panel py-16 flex items-center justify-center gap-2 text-sm text-gray-500">
 <Loader2 className="w-5 h-5 animate-spin text-ink" />
 Loading invoices…
 </div>
 ) : error ? (
 <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
 Could not load invoices.
 </div>
 ) : invoices.length === 0 ? (
 <AdminEmptyState
 icon={FileText}
 title="No invoices yet"
 description="This vendor has not created any invoices that match your filters."
 />
 ) : (
 <div className="space-y-3">
 {invoices.map((inv) => (
 <Link
 key={inv.id}
 to={`/admin/vendors/${id}/invoices/${inv.id}`}
 className="block product-panel p-4 sm:p-5 shadow-sm hover:border-ink transition-colors"
 >
 <div className="flex flex-wrap items-center gap-2 mb-2">
 <h3 className="text-sm font-semibold text-ink">
 {inv.invoice_number || `#${String(inv.id).slice(0, 8)}`}
 </h3>
 <span
 className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${statusChip( inv.status )}`}
 >
 {inv.status || 'unknown'}
 </span>
 {(inv.document_type || 'invoice') === 'receipt' && (
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-muted text-ink border border-border/80">
 Receipt
 </span>
 )}
 </div>
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="text-sm text-ink-secondary space-y-0.5">
 <p>
 <span className="text-gray-500">Client:</span>{' '}
 {inv.client_name || inv.client?.name || inv.guest_name || '—'}
 </p>
 <p>
 <span className="text-gray-500">Date:</span>{' '}
 {inv.invoice_date ? formatDate(inv.invoice_date, 'short') : '—'}
 {inv.booking?.booking_reference
 ? ` · Booking ${inv.booking.booking_reference}`
 : ''}
 </p>
 </div>
 <div className="flex items-center gap-3">
 <span className="text-base font-bold tabular-nums text-ink">
 {formatMoney(inv.total_amount, inv.currency)}
 </span>
 <span className="inline-flex items-center gap-1 text-xs font-medium text-ink">
 <Eye className="w-3.5 h-3.5" />
 View
 </span>
 </div>
 </div>
 </Link>
 ))}

 {paginator?.last_page > 1 && (
 <div className="flex items-center justify-between pt-2">
 <p className="text-xs text-gray-500">
 Page {paginator.current_page} of {paginator.last_page}
 </p>
 <div className="flex gap-2">
 <button
 type="button"
 disabled={page <= 1}
 onClick={() => setPage((p) => Math.max(1, p - 1))}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border disabled:opacity-40"
 >
 Previous
 </button>
 <button
 type="button"
 disabled={page >= paginator.last_page}
 onClick={() => setPage((p) => p + 1)}
 className="px-3 py-1.5 text-xs font-medium rounded-md border border-border disabled:opacity-40"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
};

export default AdminVendorInvoices;
